import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import { 
  Maximize2, Minimize2, Cloud, CloudOff, 
  ChevronDown, ChevronUp, RotateCcw, Eye, EyeOff, Download
} from 'lucide-react';
import { toast } from 'sonner';
import FeelingsNeedsHelper from '../FeelingsNeedsHelper';
import FloatingFeelingsHelper from '../shared/FloatingFeelingsHelper';
import {
  FeelIcon, NeedIcon, GoldIcon,
  ExerciseIcon, SaveIcon, ChapterIcon,
  ArrowRightIcon, ArrowLeftIcon, InsightIcon
} from '../icons/ElchmeIcons';
import { downloadExercisePDF, generatePage158Content } from '../../utils/pdfDownload';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=1200&q=80';

// Exercise scenarios from Page 158
const exerciseScenarios = [
  {
    id: 1,
    context: "My judgment about someone else when I think they did something wrong.",
    example: "My partner made an insulting comment about my cooking.",
    placeholders: {
      judgment: "They have no appreciation for all the effort I put in...",
      when: "my partner comments negatively on my cooking",
      feeling: "hurt",
      need: "appreciation, acknowledgment"
    }
  },
  {
    id: 2,
    context: "My judgment when someone stimulated painful feelings in me.",
    example: "My best friend told me I should not talk so much when we are with our friends.",
    placeholders: {
      judgment: "They don't value what I have to say...",
      when: "my friend suggests I talk less",
      feeling: "embarrassed, hurt",
      need: "acceptance, to be heard"
    }
  },
  {
    id: 3,
    context: "My judgment when I think someone criticized me.",
    example: "My boss said I was one of the laziest employees he ever had.",
    placeholders: {
      judgment: "He's unfair and doesn't see how hard I work...",
      when: "my boss calls me lazy",
      feeling: "shocked, discouraged",
      need: "fairness, recognition of my efforts"
    }
  },
  {
    id: 4,
    context: "My judgment when someone did something I did not want them to do.",
    example: "My mother did not return my phone call for over a week.",
    placeholders: {
      judgment: "She doesn't care about me...",
      when: "my mother doesn't return my call for a week",
      feeling: "worried, sad",
      need: "connection, reassurance"
    }
  },
  {
    id: 5,
    context: "My judgment when someone makes a demand of me.",
    example: "My son told me to stop calling him so much.",
    placeholders: {
      judgment: "He's being ungrateful after all I've done...",
      when: "my son asks me to call less",
      feeling: "hurt, confused",
      need: "connection, understanding"
    }
  },
  {
    id: 6,
    context: "My judgment when someone diagnoses me.",
    example: "My husband told me I was a narcissist.",
    placeholders: {
      judgment: "He's just trying to hurt me and doesn't understand me...",
      when: "my husband labels me a narcissist",
      feeling: "defensive, wounded",
      need: "understanding, to be seen fully"
    }
  },
  {
    id: 7,
    context: "My judgment when someone threatens me.",
    example: "My neighbor told me I had to keep my dog inside the house, so that it did not bother him, or he would call the police.",
    placeholders: {
      judgment: "He's being unreasonable and controlling...",
      when: "my neighbor threatens to call police about my dog",
      feeling: "anxious, resentful",
      need: "autonomy, respect, consideration"
    }
  }
];

export default function Page158TranslatingJudgments() {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isHelperOpen, setIsHelperOpen] = useState(false);
  const [expandedExercise, setExpandedExercise] = useState(1);
  const [showExample, setShowExample] = useState({});

  // Exercise responses
  const [responses, setResponses] = useState(() => {
    const saved = localStorage.getItem('page158Responses');
    return saved ? JSON.parse(saved) : {};
  });

  const sections = [
    { id: 'intro', title: 'Translating Judgments', page: 158 },
    { id: 'wisdom', title: "Marshall's Wisdom", page: 158 },
    { id: 'exercise-intro', title: 'Exercise Introduction', page: 158 },
    { id: 'exercises', title: 'Practice Exercises', page: 158 },
    { id: 'reflection', title: 'Reflection', page: 159 },
  ];

  const progress_pct = ((currentSection + 1) / sections.length) * 100;

  // Check login status
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  // Save progress
  const saveProgress = async () => {
    localStorage.setItem('page158Responses', JSON.stringify(responses));
    
    if (isLoggedIn) {
      setIsSyncing(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/workbook-progress/page158`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ responses })
        });
        if (response.ok) {
          setLastSyncTime(new Date());
          toast.success('Progress saved to your account!');
        }
      } catch (error) {
        toast.error('Saved locally (server sync failed)');
      } finally {
        setIsSyncing(false);
      }
    } else {
      toast.success('Saved locally! Sign in to sync across devices.');
    }
  };

  const resetAll = () => {
    if (window.confirm('Reset all Page 158 responses? This cannot be undone.')) {
      setResponses({});
      localStorage.removeItem('page158Responses');
      toast.success('All responses reset!');
    }
  };

  // Download as PDF
  const downloadPDF = () => {
    const content = generatePage158Content(responses);
    downloadExercisePDF(content, 'Translating Judgments - Page 158', 'page-158-exercise.pdf');
    toast.success('PDF download started! Use "Save as PDF" in print dialog.');
  };

  // Toggle presentation mode
  const togglePresentationMode = async () => {
    if (!isPresentationMode) {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (e) { console.log('Fullscreen not available'); }
    } else {
      try {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }
      } catch (e) { console.log('Exit fullscreen failed'); }
    }
    setIsPresentationMode(!isPresentationMode);
  };

  // Pulsing Feelings & Needs CTA Button
  const FeelingsNeedsCTA = ({ className = '' }) => (
    <Button
      onClick={() => setIsHelperOpen(true)}
      className={`relative bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white ${className}`}
      data-testid="feelings-needs-cta"
    >
      <div className="absolute inset-0 rounded-md bg-rose-500/30 animate-ping" style={{ animationDuration: '2s' }} />
      <span className="relative flex items-center gap-2">
        <FeelIcon size={18} />
        Feelings & Needs Tool
        <NeedIcon size={18} />
      </span>
    </Button>
  );

  const renderContent = () => {
    switch (currentSection) {
      // INTRO
      case 0:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="page158-intro">
            <div className="relative rounded-xl overflow-hidden shadow-2xl h-64 lg:h-80">
              <img src={HERO_IMAGE} alt="Translating Judgments" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent flex items-end p-8">
                <div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-white mb-2">Translating My Judgments</h2>
                  <p className="text-lg text-amber-200">Criticisms, Diagnoses & More → Feelings & Needs</p>
                  <p className="text-slate-300 mt-2">Page 158</p>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <FeelingsNeedsCTA className="px-6 py-3 text-lg" />
            </div>

            <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/20 border border-amber-500/30 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-amber-300 mb-4">The Foundational Insight</h3>
              <p className="text-xl text-amber-100/90 leading-relaxed italic mb-6">
                "Every judgment is a <span className="text-amber-300 font-bold">tragic expression of an unmet need</span>."
              </p>
              <p className="text-slate-300 text-lg">
                — Marshall Rosenberg
              </p>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <p className="text-slate-200 text-lg leading-relaxed">
                NVC encourages us to develop the skill of <span className="text-emerald-300 font-semibold">translating our jackal thoughts</span> into 
                our feelings and needs. This will dramatically increase the likelihood that we will get our needs met in any particular 
                moment — and the quality of our relationships will flourish.
              </p>
            </div>
          </div>
        );

      // MARSHALL'S WISDOM
      case 1:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="page158-wisdom">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-white">Marshall's Wisdom</h2>
              <FeelingsNeedsCTA />
            </div>

            {/* Miserable Quote */}
            <div className="bg-gradient-to-br from-rose-900/30 to-red-900/20 border border-rose-500/30 rounded-2xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-3xl">⚠️</span>
                </div>
                <div>
                  <p className="text-xl text-rose-100/90 leading-relaxed mb-4 italic">
                    "If you want to make yourself miserable, think about what is wrong with people."
                  </p>
                  <p className="text-xl text-rose-200/90 leading-relaxed italic">
                    "And if you want to make yourself <span className="text-rose-300 font-bold">really miserable</span>, say it out loud."
                  </p>
                </div>
              </div>
            </div>

            {/* Please/Thank You Wisdom */}
            <div className="bg-gradient-to-br from-teal-900/30 to-emerald-900/20 border border-teal-500/30 rounded-2xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-3xl">🦒</span>
                </div>
                <div>
                  <h3 className="text-teal-300 font-bold text-xl mb-3">The Hidden "Please"</h3>
                  <p className="text-xl text-teal-100/90 leading-relaxed mb-4">
                    The harshest possible statements directed towards you — if you strip away everything — they are saying 
                    <span className="text-teal-300 font-bold text-2xl"> "please"</span> (or "thank you").
                  </p>
                  <p className="text-emerald-200 text-lg">
                    <span className="font-semibold">Find out what they are saying "please" for.</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Power WITH */}
            <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/20 border border-purple-500/30 rounded-2xl p-8">
              <div className="text-center">
                <h3 className="text-purple-300 font-bold text-xl mb-4">The Goal</h3>
                <p className="text-2xl text-purple-100 leading-relaxed">
                  Finding power <span className="text-purple-300 font-bold">WITH</span> people
                </p>
                <p className="text-xl text-slate-400 mt-2">
                  instead of power <span className="line-through">FROM</span> them or <span className="line-through">AGAINST</span> them.
                </p>
              </div>
            </div>

            {/* Connection is Natural */}
            <div className="bg-gradient-to-br from-emerald-900/30 to-green-900/20 border border-emerald-500/30 rounded-2xl p-6">
              <div className="text-center">
                <p className="text-xl text-emerald-100 leading-relaxed">
                  <span className="text-emerald-300 font-bold">Connection is actually the natural way.</span>
                </p>
                <p className="text-lg text-slate-300 mt-3">
                  To be in a judgmental, criticizing, imbalanced engagement does not have to be how we are.
                </p>
                <p className="text-2xl text-emerald-300 font-bold mt-4">We can choose.</p>
              </div>
            </div>
          </div>
        );

      // EXERCISE INTRO
      case 2:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="page158-exercise-intro">
            <div className="flex items-center justify-between mb-6">
              <div className="text-center flex-1">
                <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-amber-500/20">
                  <ExerciseIcon size={40} color="#ffffff" />
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">Translation Exercise</h2>
                <p className="text-amber-300 text-lg">Page 158</p>
              </div>
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-4">Instructions</h3>
              <p className="text-slate-200 leading-relaxed mb-4">
                Write down the judgments, criticisms, demands, etc. that you have about the person or situation named. 
                Second, translate those thoughts into NVC language.
              </p>
            </div>

            {/* Example */}
            <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-6">
              <h3 className="text-blue-300 font-bold mb-4 text-lg">💡 Example</h3>
              <p className="text-slate-400 mb-3">My judgment about my partner's communication with me.</p>
              
              <div className="space-y-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-rose-300 font-semibold mb-2">Judgment:</p>
                  <p className="text-slate-300 italic">"My partner talks too much."</p>
                </div>
                
                <div className="bg-emerald-900/30 rounded-lg p-4">
                  <p className="text-emerald-300 font-semibold mb-2">Translation:</p>
                  <p className="text-slate-200">
                    <span className="text-slate-400">When</span> I think about how much my partner speaks relative to how much I speak when we are having a conversation, 
                    <span className="text-rose-300 font-semibold"> I feel</span> a lot of frustration 
                    <span className="text-emerald-300 font-semibold"> because I really value</span> being heard and understood.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <Button 
                onClick={() => setCurrentSection(3)}
                className="bg-white text-slate-900 hover:bg-slate-200 text-lg px-8 py-6 rounded-full"
              >
                Start Practice <ArrowRightIcon size={24} className="ml-2" />
              </Button>
            </div>
          </div>
        );

      // EXERCISES
      case 3:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="page158-exercises">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Translation Exercises</h2>
              <FeelingsNeedsCTA />
            </div>

            <div className="space-y-4">
              {exerciseScenarios.map((scenario) => (
                <div 
                  key={scenario.id} 
                  className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedExercise(expandedExercise === scenario.id ? null : scenario.id)}
                    className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                  >
                    <div className="flex items-center gap-3 text-left">
                      <span className="bg-amber-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                        {scenario.id}
                      </span>
                      <div>
                        <p className="text-slate-200 font-medium">{scenario.context}</p>
                        <p className="text-slate-400 text-sm mt-1 italic">e.g., "{scenario.example}"</p>
                      </div>
                    </div>
                    {expandedExercise === scenario.id ? (
                      <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    )}
                  </button>

                  {expandedExercise === scenario.id && (
                    <div className="px-5 pb-5 space-y-4 animate-in slide-in-from-top duration-200">
                      {/* Judgment */}
                      <div>
                        <label className="text-sm font-medium text-rose-300 mb-2 block">Your Judgment:</label>
                        <Textarea
                          value={responses[scenario.id]?.judgment || ''}
                          onChange={(e) => setResponses(prev => ({
                            ...prev,
                            [scenario.id]: { ...prev[scenario.id], judgment: e.target.value }
                          }))}
                          placeholder={scenario.placeholders.judgment}
                          className="bg-rose-900/20 border-rose-700/30 text-white min-h-[80px]"
                        />
                      </div>

                      {/* Translation */}
                      <div className="bg-emerald-900/20 border border-emerald-700/30 rounded-lg p-4 space-y-3">
                        <p className="text-emerald-300 font-semibold">Translation:</p>
                        
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400 text-sm w-16">When</span>
                          <Textarea
                            value={responses[scenario.id]?.when || ''}
                            onChange={(e) => setResponses(prev => ({
                              ...prev,
                              [scenario.id]: { ...prev[scenario.id], when: e.target.value }
                            }))}
                            placeholder={scenario.placeholders.when}
                            className="bg-slate-800/50 border-slate-600 text-white min-h-[60px]"
                          />
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-rose-300 text-sm w-16">I feel</span>
                          <Textarea
                            value={responses[scenario.id]?.feeling || ''}
                            onChange={(e) => setResponses(prev => ({
                              ...prev,
                              [scenario.id]: { ...prev[scenario.id], feeling: e.target.value }
                            }))}
                            placeholder={scenario.placeholders.feeling}
                            className="bg-rose-900/20 border-rose-700/30 text-white min-h-[50px]"
                          />
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-emerald-300 text-sm w-16">Because I need</span>
                          <Textarea
                            value={responses[scenario.id]?.need || ''}
                            onChange={(e) => setResponses(prev => ({
                              ...prev,
                              [scenario.id]: { ...prev[scenario.id], need: e.target.value }
                            }))}
                            placeholder={scenario.placeholders.need}
                            className="bg-emerald-900/20 border-emerald-700/30 text-white min-h-[50px]"
                          />
                        </div>
                      </div>

                      {/* Show Example Answer */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowExample(prev => ({ ...prev, [scenario.id]: !prev[scenario.id] }))}
                        className="text-slate-400 hover:text-white"
                      >
                        {showExample[scenario.id] ? (
                          <><EyeOff className="w-4 h-4 mr-2" /> Hide Example</>
                        ) : (
                          <><Eye className="w-4 h-4 mr-2" /> Show Example</>
                        )}
                      </Button>

                      {showExample[scenario.id] && (
                        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 animate-in fade-in">
                          <p className="text-blue-300 font-semibold mb-2 text-sm">Example Translation:</p>
                          <p className="text-slate-200 text-sm">
                            <span className="text-slate-400">When</span> {scenario.placeholders.when}, 
                            <span className="text-rose-300"> I feel</span> {scenario.placeholders.feeling} 
                            <span className="text-emerald-300"> because I need</span> {scenario.placeholders.need}.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      // REFLECTION
      case 4:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="page158-reflection">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-white">Reflection</h2>
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/20 border border-purple-500/30 rounded-2xl p-8">
              <InsightIcon size={32} className="text-purple-400 mb-4" />
              <h3 className="text-purple-300 font-bold text-xl mb-4">What Did You Discover?</h3>
              <Textarea
                value={responses.reflection || ''}
                onChange={(e) => setResponses(prev => ({ ...prev, reflection: e.target.value }))}
                placeholder="As you practiced translating judgments into feelings and needs, what did you notice? What patterns emerged? What was challenging? What felt liberating?"
                className="bg-slate-800/50 border-purple-700/30 text-white min-h-[150px]"
              />
            </div>

            {/* Key Takeaways */}
            <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-xl p-6">
              <h4 className="text-emerald-300 font-bold mb-4">🌟 Key Takeaways</h4>
              <ul className="space-y-3 text-slate-200">
                <li className="flex items-start gap-3">
                  <span className="text-emerald-400">•</span>
                  <span>Behind every judgment is an <span className="text-emerald-300 font-semibold">unmet need</span> longing to be acknowledged.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-400">•</span>
                  <span>Translating judgments helps us move from <span className="text-rose-300">blame</span> to <span className="text-emerald-300">connection</span>.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-400">•</span>
                  <span>This practice increases the likelihood of <span className="text-emerald-300 font-semibold">getting our needs met</span>.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-400">•</span>
                  <span>Connection is our <span className="text-teal-300 font-semibold">natural state</span> — judgment is learned, and we can choose differently.</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 text-center">
              <p className="text-slate-300 text-lg mb-4">
                You've completed <span className="text-amber-400 font-bold">Page 158: Translating Judgments</span>! 🎉
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  onClick={() => navigate('/workbook/chapter-4')}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  <ArrowLeftIcon size={18} className="mr-2" />
                  Back to Chapter 4
                </Button>
                <Button
                  onClick={() => navigate('/belonging-ebook')}
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white"
                >
                  <ChapterIcon size={18} className="mr-2" />
                  Return to eBook
                </Button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 ${isPresentationMode ? 'overflow-hidden' : ''}`}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeftIcon size={16} className="mr-1" />
                Back
              </Button>
              <div className="h-5 w-px bg-slate-700" />
              <div>
                <h1 className="text-lg font-bold text-white">Translating Judgments</h1>
                <p className="text-xs text-amber-400">Page 158 • Featured Resource</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {isLoggedIn && (
                <div className="flex items-center gap-1 text-xs text-slate-400">
                  {isSyncing ? (
                    <Cloud className="w-4 h-4 animate-pulse text-blue-400" />
                  ) : lastSyncTime ? (
                    <Cloud className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <CloudOff className="w-4 h-4 text-slate-500" />
                  )}
                </div>
              )}
              
              <Button onClick={saveProgress} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <SaveIcon size={16} className="mr-1" />
                Save
              </Button>
              <Button onClick={downloadPDF} variant="ghost" size="sm" className="text-slate-400 hover:text-white" title="Download as PDF">
                <Download className="w-4 h-4 mr-1" />
                PDF
              </Button>
              <Button onClick={resetAll} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <RotateCcw className="w-4 h-4" />
              </Button>
              <Button onClick={togglePresentationMode} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                {isPresentationMode ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-3">
            <Progress value={progress_pct} className="h-1.5 bg-slate-700" />
            <div className="flex justify-between mt-1 text-xs text-slate-500">
              <span>Section {currentSection + 1} of {sections.length}</span>
              <span>Page {sections[currentSection]?.page}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Section Navigation */}
      {!isPresentationMode && (
        <div className="bg-slate-800/50 border-b border-slate-700/30">
          <div className="max-w-5xl mx-auto px-4 py-2">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {sections.map((section, idx) => (
                <button
                  key={section.id}
                  onClick={() => setCurrentSection(idx)}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-all ${
                    currentSection === idx
                      ? 'bg-amber-600 text-white'
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600/50 hover:text-slate-200'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className={`max-w-4xl mx-auto px-4 py-8 ${isPresentationMode ? 'h-screen flex items-center' : ''}`}>
        <div className="w-full">
          {renderContent()}
        </div>
      </main>

      {/* Navigation Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-md border-t border-slate-700/50 py-3 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button
            onClick={() => setCurrentSection(prev => Math.max(prev - 1, 0))}
            disabled={currentSection === 0}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800 disabled:opacity-30"
          >
            <ArrowLeftIcon size={18} className="mr-2" />
            Previous
          </Button>
          
          <div className="text-center">
            <span className="text-slate-400 text-sm">{sections[currentSection]?.title}</span>
          </div>
          
          <Button
            onClick={() => setCurrentSection(prev => Math.min(prev + 1, sections.length - 1))}
            disabled={currentSection === sections.length - 1}
            className="bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-30"
          >
            Next
            <ArrowRightIcon size={18} className="ml-2" />
          </Button>
        </div>
      </div>

      {/* Floating Feelings & Needs Helper */}
      <FloatingFeelingsHelper />

      {/* Feelings & Needs Helper Modal */}
      <FeelingsNeedsHelper 
        isOpen={isHelperOpen} 
        onClose={() => setIsHelperOpen(false)} 
      />
    </div>
  );
}
